/*
 * Host element is zero-size + fixed inset so launcher/panel (position:fixed inside shadow DOM)
 * are not clipped by MediKaLLink layout containers (overflow/transform).
 */
:host {
  position: fixed !important;
  inset: 0 !important;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: visible !important;
  pointer-events: none !important;
  z-index: 2147483646 !important;
  background: transparent !important;
}

.vnw-widgetRoot {
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  color: var(--vnw-text);
}

.vnw-launcher,
.vnw-panel,
.vnw-modalityPreview,
.vnw-wakePhraseNotice,
.vnw-wakeHeardDebug {
  pointer-events: auto;
}

.vnw-mountRenderError {
  position: fixed;
  right: 18px;
  bottom: 86px;
  max-width: 320px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  pointer-events: auto;
  z-index: 2147483002;
}

.vnw-widgetRoot,
.vnw-widgetRoot * {
  box-sizing: border-box;
}

/* Temporary: session pending-commands line after full-page reload (client-only, not persisted). */
.vnw-sessionPendingDebug {
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px dashed rgba(100, 116, 139, 0.55);
  background: rgba(241, 245, 249, 0.95);
}
.vnw-sessionPendingDebugText {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.45;
  color: var(--vnw-mutedText, #475569);
  white-space: pre-wrap;
  word-break: break-word;
}

/*
 * TEMPORARY TESTING: mock patient page behind chat (open_patient + MOCK_PATIENT_PAGE).
 */
.vnw-mockPatientBackdrop {
  position: fixed;
  inset: 0;
  z-index: 2147482970;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(160deg, #e8eef5 0%, #cfd9e6 45%, #b8c5d6 100%);
  pointer-events: auto;
}

.vnw-mockPatientCard {
  width: min(520px, 100%);
  padding: 28px 32px 24px;
  border-radius: 12px;
  background: #fff;
  box-shadow:
    0 4px 24px rgba(15, 23, 42, 0.12),
    0 1px 3px rgba(15, 23, 42, 0.08);
}

.vnw-mockPatientEyebrow {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.vnw-mockPatientTitle {
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.vnw-mockPatientFields {
  margin: 0 0 24px;
  padding: 0;
}

.vnw-mockPatientFields > div {
  margin-bottom: 14px;
}

.vnw-mockPatientFields > div:last-child {
  margin-bottom: 0;
}

.vnw-mockPatientFields dt {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.vnw-mockPatientFields dd {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.vnw-mockPatientClose {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.vnw-mockPatientClose:hover {
  background: #f1f5f9;
}

/* AWS Polly read-aloud controls (above composer when inbox backdrop is active). */
.vnw-readAloudBar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  padding: 8px 10px 10px;
  margin: 0;
  border-top: 1px solid var(--vnw-border);
  background: linear-gradient(to bottom, rgba(248, 250, 252, 0.96), var(--vnw-panelBg));
}

.vnw-readAloudBarLabel {
  font-size: 0.6875rem;
  font-weight: 650;
  color: var(--vnw-mutedText);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.vnw-readAloudBarBtns {
  display: flex;
  align-items: center;
  gap: 6px;
}

.vnw-readAloudBtn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-textareaBg);
  color: var(--vnw-text);
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
}

.vnw-readAloudBtn:hover:not(:disabled) {
  border-color: var(--vnw-primary);
  color: var(--vnw-primary);
}

.vnw-readAloudBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-readAloudBarStatus {
  font-size: 0.75rem;
  color: var(--vnw-mutedText);
}

.vnw-readAloudBarError {
  font-size: 0.75rem;
  color: #b91c1c;
  flex: 1 1 100%;
}

/* Modality file (ECG, etc.): iframe docked beside chat panel, under panel z-index for stacking. */
.vnw-modalityPreview {
  position: fixed;
  z-index: 2147482999;
  /* Wide/tall enough for typical ECG strips; capped so preview + 360px chat + margins fit in viewport. */
  width: min(760px, calc(100vw - 396px));
  height: min(720px, calc(100vh - 48px));
  max-height: calc(100vh - 48px);
  border: 1px solid var(--vnw-border);
  border-radius: 14px;
  background: var(--vnw-panelBg);
  box-shadow: 0 20px 60px var(--vnw-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.vnw-modalityPreviewHeader {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px 14px;
  border-bottom: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
}

.vnw-modalityPreviewTitle {
  font-weight: 650;
  font-size: 13px;
  color: var(--vnw-text);
}

.vnw-modalityPreviewActions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.vnw-modalityPreviewHeaderBtn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--vnw-text);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.vnw-modalityPreviewHeaderBtn:hover {
  border-color: var(--vnw-border);
  background: var(--vnw-textareaBg);
}

.vnw-modalityPreviewHeaderBtn:focus-visible {
  outline: 3px solid var(--vnw-primary);
  outline-offset: 3px;
}

/* Nearly full viewport; above chat panel so the ECG uses the whole screen. */
.vnw-widgetRoot .vnw-modalityPreview.vnw-modalityPreview--maximized {
  left: 10px !important;
  right: 10px !important;
  top: 10px !important;
  bottom: 10px !important;
  width: auto !important;
  height: auto !important;
  max-height: none !important;
  z-index: 2147483010 !important;
  border-radius: 12px;
}

.vnw-modalityPreviewFrame {
  flex: 1 1 auto;
  min-height: 0;
  background: #0f172a;
}

.vnw-modalityPreviewIframe {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.vnw-widgetRoot[data-position='bottom-right'] .vnw-modalityPreview {
  right: 390px;
  bottom: 86px;
}

.vnw-widgetRoot[data-position='bottom-left'] .vnw-modalityPreview {
  left: 390px;
  bottom: 86px;
}

.vnw-widgetRoot[data-position='top-right'] .vnw-modalityPreview {
  right: 390px;
  top: 86px;
}

.vnw-widgetRoot[data-position='top-left'] .vnw-modalityPreview {
  left: 390px;
  top: 86px;
}

@media (max-width: 860px) {
  .vnw-widgetRoot[data-position='bottom-right'] .vnw-modalityPreview,
  .vnw-widgetRoot[data-position='bottom-left'] .vnw-modalityPreview {
    left: 18px;
    right: 18px;
    width: auto;
    height: min(480px, 52vh);
    max-height: min(480px, 52vh);
    bottom: calc(86px + min(560px, calc(100vh - 120px)) + 10px);
  }

  .vnw-widgetRoot[data-position='top-right'] .vnw-modalityPreview,
  .vnw-widgetRoot[data-position='top-left'] .vnw-modalityPreview {
    left: 18px;
    right: 18px;
    width: auto;
    height: min(480px, 52vh);
    max-height: min(480px, 52vh);
    top: calc(86px + min(560px, calc(100vh - 120px)) + 10px);
  }
}

.vnw-launcher {
  position: fixed;
  z-index: 2147483000;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: none;
  background: var(--vnw-launcherBg);
  color: #fff;
  box-shadow: 0 14px 35px var(--vnw-shadow);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform 120ms ease, background 120ms ease;
}

.vnw-launcher:active {
  transform: translateY(1px);
}

.vnw-launcher:focus-visible {
  outline: 3px solid var(--vnw-primary);
  outline-offset: 3px;
}

.vnw-widgetRoot[data-position='bottom-right'] .vnw-launcher {
  right: 18px;
  bottom: 18px;
}
.vnw-widgetRoot[data-position='bottom-left'] .vnw-launcher {
  left: 18px;
  bottom: 18px;
}
.vnw-widgetRoot[data-position='top-right'] .vnw-launcher {
  right: 18px;
  top: 18px;
}
.vnw-widgetRoot[data-position='top-left'] .vnw-launcher {
  left: 18px;
  top: 18px;
}

.vnw-panel {
  position: fixed;
  z-index: 2147483001;
  width: 360px;
  height: 560px;
  max-width: calc(100vw - 36px);
  max-height: calc(100vh - 120px);
  border: 1px solid var(--vnw-border);
  border-radius: 14px;
  background: var(--vnw-panelBg);
  box-shadow: 0 20px 60px var(--vnw-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: bottom right;
  transition: opacity 140ms ease, transform 160ms ease;
}

.vnw-panel[data-open='false'] {
  opacity: 0;
  pointer-events: none;
  transform: scale(0.98) translateY(10px);
}

.vnw-panel[data-open='true'] {
  opacity: 1;
  pointer-events: auto;
  transform: scale(1) translateY(0);
}

.vnw-widgetRoot[data-position='bottom-right'] .vnw-panel {
  right: 18px;
  bottom: 86px;
}
.vnw-widgetRoot[data-position='bottom-left'] .vnw-panel {
  left: 18px;
  bottom: 86px;
}
.vnw-widgetRoot[data-position='top-right'] .vnw-panel {
  right: 18px;
  top: 86px;
  transform-origin: top right;
}
.vnw-widgetRoot[data-position='top-left'] .vnw-panel {
  left: 18px;
  top: 86px;
  transform-origin: top left;
}

.vnw-panelHeader {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 10px 14px;
  border-bottom: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
}

.vnw-panelTitle {
  font-weight: 650;
  letter-spacing: 0.1px;
  font-size: 14px;
}

.vnw-panelTitleWrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.vnw-panelSubtitle {
  font-size: 11px;
  color: var(--vnw-mutedText);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vnw-panelHeaderActions {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
  margin-left: 8px;
}

.vnw-panelHeaderActionDivider {
  width: 1px;
  height: 22px;
  margin: 0 10px;
  background: var(--vnw-border, #e2e8f0);
  flex-shrink: 0;
}

.vnw-cancelIntentBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px 0 8px;
  border-radius: 9px;
  border: 1px solid color-mix(in srgb, var(--vnw-border, #e2e8f0) 90%, var(--vnw-text, #0f172a) 10%);
  background: color-mix(in srgb, var(--vnw-panelBg, #fff) 88%, var(--vnw-border, #e2e8f0) 12%);
  color: var(--vnw-mutedText, #64748b);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  white-space: nowrap;
}

.vnw-cancelIntentBtnIcon {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: color-mix(in srgb, var(--vnw-mutedText, #64748b) 85%, var(--vnw-text, #0f172a) 15%);
}

.vnw-cancelIntentBtnLabel {
  padding-right: 1px;
}

.vnw-cancelIntentBtn:hover:not(:disabled) {
  color: var(--vnw-text, #0f172a);
  border-color: color-mix(in srgb, var(--vnw-border, #e2e8f0) 70%, var(--vnw-text, #0f172a) 30%);
  background: var(--vnw-textareaBg, #f1f5f9);
}

.vnw-cancelIntentBtn:active:not(:disabled) {
  transform: translateY(0.5px);
}

.vnw-cancelIntentBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-closeBtn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--vnw-text);
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.vnw-cancelIntentBtn:focus-visible,
.vnw-closeBtn:focus-visible {
  outline: 3px solid var(--vnw-primary);
  outline-offset: 3px;
}

.vnw-closeBtn:hover {
  border-color: var(--vnw-border);
  background: var(--vnw-textareaBg);
}

.vnw-panelBody {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.vnw-statusLine {
  padding: 10px 14px 6px 14px;
  color: var(--vnw-mutedText);
  font-size: 12px;
}

.vnw-navFallback {
  margin: 0 12px 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--vnw-border);
  background: #fffbeb;
  color: var(--vnw-text);
  font-size: 12px;
  line-height: 1.4;
}

.vnw-navFallbackText {
  margin: 0 0 8px;
}

.vnw-navFallbackBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 650;
  background: var(--vnw-primary);
  color: var(--vnw-primaryText);
}

.vnw-navFallbackBtn:hover {
  filter: brightness(1.05);
}

.vnw-intentUrlDebugLink {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  word-break: break-all;
}

.vnw-intentUrlDebugLink:hover {
  text-decoration: none;
}

.vnw-messageList {
  flex: 1 1 auto;
  overflow: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding: 10px 12px;
  background: var(--vnw-textareaBg);
}

.vnw-message {
  margin: 8px 0;
  display: flex;
}

.vnw-messageBubble {
  max-width: 85%;
  padding: 10px 11px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-size: 13px;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
}

.vnw-message--user {
  justify-content: flex-end;
}
.vnw-message--user .vnw-messageBubble {
  background: #eaf2ff;
  border-color: #cfe0ff;
  color: var(--vnw-text);
}

.vnw-message--user.vnw-message--awaitingResponse .vnw-messageBubble {
  border-color: #93b4f5;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.12);
}

.vnw-message--assistant {
  justify-content: flex-start;
}
.vnw-message--assistant .vnw-messageBubble {
  background: var(--vnw-panelBg);
  border-color: var(--vnw-border);
  color: var(--vnw-text);
}

.vnw-message--pendingModalityExam .vnw-messageBubble {
  border-left: 3px solid rgba(59, 130, 246, 0.65);
}

.vnw-messageColumn {
  display: flex;
  flex-direction: column;
  max-width: 100%;
}

.vnw-messageColumn--user {
  align-items: flex-end;
}

.vnw-messageColumn--assistant {
  align-items: flex-start;
  width: 100%;
  min-width: 0;
}

/* WhatsApp-style: time under the bubble edge — left for assistant, right for user. */
.vnw-messageGap {
  align-self: center;
  margin: 6px 0 2px;
  padding: 2px 8px;
  font-size: 10px;
  line-height: 1.2;
  color: var(--vnw-mutedText);
  opacity: 0.85;
  letter-spacing: 0.02em;
}

.vnw-messageFooterTime {
  display: block;
  margin-top: 3px;
  padding-inline: 2px;
  font-size: 11px;
  line-height: 1.25;
  color: var(--vnw-mutedText);
}

.vnw-messageColumn--assistant .vnw-messageFooterTime {
  align-self: flex-start;
}

.vnw-messageColumn--user .vnw-messageFooterTime {
  align-self: flex-end;
}

.vnw-messageStack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: 100%;
}

.vnw-modalityMeta {
  max-width: 85%;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-textareaBg);
  font-size: 12px;
  line-height: 1.4;
  color: var(--vnw-mutedText);
}

.vnw-modalityMetaLine {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
  word-break: break-word;
}

.vnw-modalityMetaLine + .vnw-modalityMetaLine {
  margin-top: 6px;
}

.vnw-modalityMetaLabel {
  flex-shrink: 0;
  font-weight: 600;
  color: var(--vnw-text);
}

.vnw-modalityMetaLine--file {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.vnw-modalityMetaUrl {
  display: block;
  word-break: break-all;
  overflow-wrap: anywhere;
  user-select: all;
  color: var(--vnw-text);
  font-size: 11px;
  line-height: 1.45;
}

/* Prescription: single bubble, intent + type always visible */
.vnw-prescriptionStructured {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.vnw-prescriptionStructuredLine {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  line-height: 1.35;
}

.vnw-prescriptionStructuredLine--message {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: 2px;
}

.vnw-prescriptionStructuredLabel {
  flex: 0 0 auto;
  font-weight: 650;
  color: var(--vnw-text);
  min-width: 7.5rem;
}

.vnw-prescriptionStructuredValue {
  flex: 1 1 auto;
  color: var(--vnw-primary);
  font-weight: 600;
  word-break: break-word;
}

.vnw-prescriptionStructuredLine--message .vnw-prescriptionStructuredValue {
  color: var(--vnw-mutedText);
  font-weight: 400;
}

/* Sports NOC: use full chat row width so the value column is not squeezed */
.vnw-message--sportsNocRow {
  align-self: stretch;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.vnw-message--sportsNocRow .vnw-messageStack--sportsNoc {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

/* Sports NOC: full-width card (outside the 85% message bubble) */
.vnw-messageStack--sportsNoc {
  width: 100%;
  max-width: 100%;
  align-items: stretch;
  gap: 10px;
}

.vnw-sportsNocCardSlot {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.vnw-messageBubble--sportsNocPrompt {
  max-width: 100%;
  width: 100%;
}

/* Sports NOC summary: uniform key | value | (optional Edit) */
.vnw-sportsNocKvSummary {
  padding-top: 0;
}

.vnw-sportsNocKvSummary.vnw-secretaryMessagePreview {
  width: 100%;
  max-width: 100%;
}

.vnw-sportsNocKvCard {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-width: 0;
}

.vnw-sportsNocKvRow {
  display: grid;
  /* Narrow keys, wide values; third column only as wide as the Edit button (not a fixed min) */
  grid-template-columns: minmax(3.25rem, 5.5rem) minmax(0, 1fr) auto;
  gap: 8px 10px;
  align-items: start;
  padding: 9px 0;
  border-bottom: 1px solid var(--vnw-border, #e2e8f0);
  font-size: 13px;
  line-height: 1.4;
  min-width: 0;
}

.vnw-sportsNocKvRow:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.vnw-sportsNocKvRow:first-child {
  padding-top: 0;
}

.vnw-sportsNocKvKey {
  font-weight: 650;
  color: var(--vnw-mutedText, #64748b);
  font-size: 12px;
  letter-spacing: 0.02em;
  max-width: 5.75rem;
  overflow-wrap: break-word;
  hyphens: auto;
}

.vnw-sportsNocKvVal {
  color: var(--vnw-text, #0f172a);
  font-weight: 500;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: normal;
}

.vnw-sportsNocKvVal--multiline {
  white-space: pre-wrap;
}

.vnw-sportsNocKvAction {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

.vnw-sportsNocKvEdit {
  font-size: 0.75rem;
  font-weight: 650;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-textareaBg);
  color: var(--vnw-text);
  cursor: pointer;
  white-space: nowrap;
}

.vnw-sportsNocKvEdit:hover {
  border-color: var(--vnw-primary);
  color: var(--vnw-primary);
}

.vnw-sportsNocGenerating {
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--vnw-textareaBg, #f8fafc);
  border: 1px dashed var(--vnw-border, #e2e8f0);
  color: var(--vnw-muted, #64748b);
  font-size: 0.9375rem;
  font-style: italic;
}

.vnw-message--sportsNocGenerating .vnw-sportsNocGenerating {
  margin-top: 4px;
}

/* In-chat Yes / No under sports NOC summary */
.vnw-sportsNocYesNo {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--vnw-border, #e2e8f0);
}

.vnw-sportsNocYesNoBtn {
  flex: 1 1 120px;
  min-height: 42px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 650;
  cursor: pointer;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-textareaBg);
  color: var(--vnw-text);
}

.vnw-sportsNocYesNoBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-sportsNocYesNoBtn--yes {
  border-color: var(--vnw-primary);
  color: var(--vnw-primary);
}

.vnw-sportsNocYesNoBtn--yes:hover:not(:disabled) {
  background: var(--vnw-primary);
  color: var(--vnw-primaryText, #fff);
}

.vnw-sportsNocYesNoBtn--no:hover:not(:disabled) {
  border-color: var(--vnw-mutedText);
}

.vnw-sportsNocKvSummary--generatePreview {
  border-color: color-mix(in srgb, var(--vnw-primary) 28%, var(--vnw-border, #e2e8f0));
}

.vnw-sportsNocPreviewTitle {
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--vnw-mutedText, #64748b);
  margin-bottom: 10px;
}

.vnw-sportsNocKvSummary--editing .vnw-sportsNocKvRow {
  align-items: start;
}

.vnw-sportsNocSummaryInlineBody {
  grid-column: 2 / -1;
  min-width: 0;
}

.vnw-sportsNocSummaryInlineActions {
  margin-top: 10px;
}

.vnw-sportsNocSummaryInlineSports {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vnw-sportsNocSummaryInlineSportBtn {
  width: 100%;
  justify-content: flex-start;
}

.vnw-sportsNocLocationOptions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  width: 100%;
  margin-top: 10px;
}

.vnw-sportsNocLocationOptions--resolved {
  opacity: 0.72;
}

.vnw-sportsNocLocationOptionsResolvedNote {
  grid-column: 1 / -1;
  font-size: 0.75rem;
  color: var(--vnw-mutedText, #64748b);
  margin-bottom: 2px;
}

.vnw-sportsNocLocationOptionIndex,
.vnw-sportsNocSportOptionIndex {
  flex: 0 0 auto;
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--vnw-mutedText, #64748b);
  min-width: 1.1rem;
}

.vnw-sportsNocLocationOptionBtn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-height: 56px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-textareaBg, #f8fafc);
  color: var(--vnw-text);
  cursor: pointer;
  text-align: center;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.vnw-sportsNocLocationOptionBtn:hover:not(:disabled) {
  border-color: var(--vnw-primary);
  background: color-mix(in srgb, var(--vnw-primary) 8%, var(--vnw-textareaBg, #f8fafc));
  box-shadow: 0 2px 8px rgb(15 23 42 / 0.06);
}

.vnw-sportsNocLocationOptionBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-sportsNocLocationOptionLabel {
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 650;
  line-height: 1.25;
  text-align: left;
}

.vnw-sportsNocEditUiLabel {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vnw-mutedText, #64748b);
  margin-bottom: 0.35rem;
}

.vnw-sportsNocLocationDropdownWrap {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.25rem;
}

.vnw-sportsNocLocationDropdownLabel,
.vnw-sportsNocCommentsInputLabel {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vnw-mutedText, #64748b);
}

.vnw-sportsNocLocationDropdown {
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-radius: 8px;
  background: var(--vnw-surface, #fff);
  font: inherit;
}

.vnw-sportsNocCommentsInput {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.25rem;
}

.vnw-sportsNocCommentsActions {
  display: flex;
  flex-direction: row;
  gap: 0.55rem;
  align-items: center;
}

.vnw-sportsNocCommentsTextarea {
  width: 100%;
  min-height: 4.5rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-radius: 8px;
  font: inherit;
  resize: vertical;
}

.vnw-sportsNocCommentsInlineWrap {
  align-items: stretch;
}

.vnw-sportsNocCommentsTextarea--inline {
  flex: 1 1 auto;
  min-width: 0;
}

.vnw-sportsNocCommentsSave {
  align-self: flex-start;
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  border: 1px solid var(--vnw-border, #cbd5e1);
  background: var(--vnw-surface, #fff);
  cursor: pointer;
  font: inherit;
}

.vnw-sportsNocCommentsNoBtn {
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  border: 1px solid var(--vnw-border, #cbd5e1);
  background: var(--vnw-surface, #fff);
  cursor: pointer;
  font: inherit;
  color: var(--vnw-mutedText, #64748b);
}

.vnw-sportsNocCommentsNoBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-sportsNocCommentsPromptActions {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 0.35rem;
}

.vnw-sportsNocCommentsSave:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-sportsNocSportOptions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  width: 100%;
  margin-top: 10px;
}

.vnw-sportsNocSportOptions--resolved {
  opacity: 0.72;
}

.vnw-sportsNocSportOptionsResolvedNote {
  grid-column: 1 / -1;
  font-size: 0.75rem;
  color: var(--vnw-mutedText, #64748b);
  margin-bottom: 2px;
}

.vnw-sportsNocSportOptionBtn {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-textareaBg, #f8fafc);
  color: var(--vnw-text);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.vnw-sportsNocSportOptionBtn--selected {
  border-color: var(--vnw-primary);
  background: color-mix(in srgb, var(--vnw-primary) 10%, var(--vnw-textareaBg, #f8fafc));
}

.vnw-sportsNocSportOptionBtn:hover:not(:disabled) {
  border-color: var(--vnw-primary);
  box-shadow: 0 2px 8px rgb(15 23 42 / 0.06);
}

.vnw-sportsNocSportOptionBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-sportsNocSportOptionTick {
  flex: 0 0 1.25rem;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid var(--vnw-border, #cbd5e1);
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1;
  color: var(--vnw-primary);
}

.vnw-sportsNocSportOptionBtn--selected .vnw-sportsNocSportOptionTick {
  border-color: var(--vnw-primary);
  background: var(--vnw-primary);
  color: var(--vnw-primaryText, #fff);
}

.vnw-sportsNocSportOptionLabel {
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 650;
  line-height: 1.25;
}

.vnw-sportsNocSportOptionsConfirm {
  grid-column: 1 / -1;
  min-height: 44px;
  margin-top: 4px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid var(--vnw-primary);
  background: var(--vnw-primary);
  color: var(--vnw-primaryText, #fff);
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: pointer;
}

.vnw-sportsNocSportOptionsConfirm:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-sportsNocSportOptionsConfirm:hover:not(:disabled) {
  filter: brightness(1.05);
}

.vnw-sportsNocActionCards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  margin-top: 10px;
}

.vnw-sportsNocActionCards--singleColumn {
  grid-template-columns: minmax(0, 1fr);
}

.vnw-sportsNocActionCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  min-height: 72px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-textareaBg, #fff);
  cursor: pointer;
  text-align: left;
}

.vnw-sportsNocActionCard:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vnw-sportsNocActionCardTitle {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--vnw-text, #0f172a);
}

.vnw-sportsNocActionCardHint {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--vnw-mutedText, #64748b);
  line-height: 1.35;
}

.vnw-sportsNocActionCard--yes {
  border-color: color-mix(in srgb, var(--vnw-primary) 55%, var(--vnw-border, #e2e8f0));
}

.vnw-sportsNocActionCard--yes:hover:not(:disabled) {
  background: var(--vnw-primary);
  border-color: var(--vnw-primary);
}

.vnw-sportsNocActionCard--yes:hover:not(:disabled) .vnw-sportsNocActionCardTitle,
.vnw-sportsNocActionCard--yes:hover:not(:disabled) .vnw-sportsNocActionCardHint {
  color: var(--vnw-primaryText, #fff);
}

.vnw-sportsNocActionCard--no:hover:not(:disabled) {
  border-color: var(--vnw-mutedText, #64748b);
}

/* Reminder set successfully: success hero (check) + card */
.vnw-reminderSuccessHero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 14px 16px;
  margin: -1px -1px 0 -1px;
  border-radius: 10px 10px 0 0;
  background: linear-gradient(
    118deg,
    color-mix(in srgb, var(--vnw-successText, #16a34a) 14%, transparent) 0%,
    color-mix(in srgb, var(--vnw-panelSubtleBg, #f8fafc) 88%, #fff) 100%
  );
  border-bottom: 1px solid color-mix(in srgb, var(--vnw-successText, #15803d) 24%, var(--vnw-border, #e2e8f0));
}

.vnw-reminderSuccessHeroIcon {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vnw-reminderSuccessHeroSvg {
  width: 48px;
  height: 48px;
  display: block;
}

.vnw-reminderSuccessHeroSvgCircle {
  fill: color-mix(in srgb, var(--vnw-successText, #22c55e) 12%, #fff);
  stroke: color-mix(in srgb, var(--vnw-successText, #15803d) 45%, #bbf7d0);
  stroke-width: 1.2;
}

.vnw-reminderSuccessHeroSvgCheck {
  stroke: var(--vnw-successText, #15803d);
}

.vnw-reminderSuccessHeroText {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.vnw-reminderSuccessHeroTitle {
  font-size: 1.06rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vnw-successText, #166534);
  line-height: 1.25;
  margin-bottom: 4px;
}

.vnw-reminderSuccessHeroSub {
  font-size: 0.88rem;
  color: var(--vnw-mutedText, #64748b);
  line-height: 1.45;
}

@media (prefers-reduced-motion: no-preference) {
  .vnw-reminderSuccessHeroIcon {
    animation: vnw-reminderSuccessPop 0.52s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
}

@keyframes vnw-reminderSuccessPop {
  0% {
    transform: scale(0.82);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Cancel / all-clear notices: information hero */
.vnw-reminderCancelledHero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 14px 16px;
  margin: -1px -1px 0 -1px;
  border-radius: 10px 10px 0 0;
  background: linear-gradient(
    118deg,
    color-mix(in srgb, var(--vnw-accent, #2563eb) 12%, transparent) 0%,
    color-mix(in srgb, var(--vnw-panelSubtleBg, #f8fafc) 88%, #fff) 100%
  );
  border-bottom: 1px solid color-mix(in srgb, var(--vnw-accent, #2563eb) 22%, var(--vnw-border, #e2e8f0));
}

.vnw-reminderCancelledHeroIcon {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vnw-reminderCancelledHeroSvg {
  width: 48px;
  height: 48px;
  display: block;
}

.vnw-reminderCancelledHeroSvgCircle {
  fill: color-mix(in srgb, var(--vnw-accent, #3b82f6) 10%, #fff);
  stroke: color-mix(in srgb, var(--vnw-accent, #2563eb) 40%, #bfdbfe);
  stroke-width: 1.2;
}

.vnw-reminderCancelledHeroSvgInfoMark {
  fill: var(--vnw-accent, #2563eb);
}

.vnw-reminderCancelledHeroSvgInfoStem {
  stroke: var(--vnw-accent, #2563eb);
}

.vnw-reminderCancelledHeroText {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}

.vnw-reminderCancelledHeroTitle {
  font-size: 1.06rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vnw-accent, #1d4ed8);
  line-height: 1.25;
  margin-bottom: 4px;
}

.vnw-reminderCancelledHeroSub {
  font-size: 0.88rem;
  color: var(--vnw-mutedText, #64748b);
  line-height: 1.45;
}

.vnw-cancelledEventShell {
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--vnw-accent, #2563eb) 28%, var(--vnw-border, #e2e8f0));
  background: var(--vnw-panelBg, #fff);
  overflow: hidden;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--vnw-accent, #2563eb) 8%, transparent),
    0 8px 24px color-mix(in srgb, var(--vnw-accent, #2563eb) 8%, transparent);
}

.vnw-reminderPreviewCard--cancelled {
  border-color: color-mix(in srgb, var(--vnw-accent, #2563eb) 34%, var(--vnw-border, #e2e8f0));
  background: var(--vnw-panelBg, #fff);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--vnw-accent, #2563eb) 10%, transparent),
    0 12px 36px color-mix(in srgb, var(--vnw-accent, #2563eb) 10%, transparent);
}

.vnw-reminderPreviewCard--cancelled .vnw-sportsNocKvAction {
  display: none;
}

.vnw-reminderPreviewCard--cancelled .vnw-sportsNocKvRow {
  border-bottom-color: color-mix(in srgb, var(--vnw-accent, #2563eb) 12%, var(--vnw-border, #e2e8f0));
}

@media (prefers-reduced-motion: no-preference) {
  .vnw-reminderCancelledHeroIcon {
    animation: vnw-reminderSuccessPop 0.52s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
}

.vnw-reminderPreviewCard--confirmed {
  border-color: color-mix(in srgb, var(--vnw-successText, #15803d) 38%, var(--vnw-border, #e2e8f0));
  background: var(--vnw-panelBg, #fff);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--vnw-successText, #22c55e) 12%, transparent),
    0 12px 36px color-mix(in srgb, var(--vnw-successText, #16a34a) 12%, transparent);
}

/* Pipeline success (mail / message): hero only — full rounding inside confirmed chrome */
.vnw-mailDocumentSuccessShell.vnw-reminderPreviewCard--confirmed .vnw-reminderSuccessHero,
.vnw-secretaryMessageSuccessShell.vnw-reminderPreviewCard--confirmed .vnw-reminderSuccessHero {
  border-radius: 10px;
  margin: -1px;
  border-bottom: none;
}

.vnw-reminderPreviewCard--confirmed .vnw-sportsNocKvAction {
  display: none;
}

.vnw-reminderPreviewCard--confirmed .vnw-sportsNocKvRow {
  border-bottom-color: color-mix(in srgb, var(--vnw-successText, #15803d) 14%, var(--vnw-border, #e2e8f0));
}

.vnw-reminderPreviewCard--confirmed .vnw-sportsNocKvCard {
  background: color-mix(in srgb, var(--vnw-panelSubtleBg, #f8fafc) 65%, var(--vnw-panelBg, #fff));
  border-radius: 0 0 8px 8px;
}

.vnw-reminderSuccessDetails .vnw-sportsNocKvAction {
  display: none;
}

.vnw-reminderSuccessDetails .vnw-sportsNocKvRow:last-child {
  border-bottom: none;
}

.vnw-reminderInlineEditRow {
  display: block;
  padding: 10px 12px 12px;
}

.vnw-reminderInlineEditRow .vnw-sportsNocKvKey {
  margin-bottom: 6px;
}

.vnw-reminderInlineEditBody {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.vnw-reminderInlineEditLabel {
  font-size: 12px;
  font-weight: 600;
  color: var(--vnw-mutedText, #64748b);
}

.vnw-reminderInlineEditInputWrap {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.vnw-reminderInlineEditInput {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 14px;
  background: var(--vnw-panelBg, #fff);
  color: inherit;
}

.vnw-reminderInlineEditInput--structured {
  width: 100%;
}

.vnw-reminderInlineEditInput:focus {
  outline: 2px solid color-mix(in srgb, var(--vnw-accent, #2563eb) 45%, transparent);
  outline-offset: 1px;
  border-color: var(--vnw-accent, #2563eb);
}

.vnw-reminderInlineEditMic {
  flex: 0 0 auto;
  width: 40px;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-radius: 8px;
  background: var(--vnw-panelBg, #fff);
  color: var(--vnw-mutedText, #64748b);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.vnw-reminderInlineEditMic--active {
  color: var(--vnw-accent, #2563eb);
  border-color: color-mix(in srgb, var(--vnw-accent, #2563eb) 55%, var(--vnw-border, #cbd5e1));
  background: color-mix(in srgb, var(--vnw-accent, #2563eb) 8%, var(--vnw-panelBg, #fff));
}

.vnw-reminderInlineEditMic:disabled {
  opacity: 0.45;
}

.vnw-reminderInlineEditHelper {
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: var(--vnw-mutedText, #64748b);
}

.vnw-reminderInlineEditHelper--error {
  color: var(--vnw-dangerText, #b91c1c);
}

.vnw-reminderInlineEditActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.vnw-reminderInlineEditBtn {
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
}

.vnw-reminderInlineEditBtn--primary {
  background: var(--vnw-accent, #2563eb);
  color: #fff;
}

.vnw-reminderInlineEditBtn--primary:disabled {
  opacity: 0.5;
}

.vnw-reminderInlineEditBtn--ghost {
  background: transparent;
  border-color: var(--vnw-border, #cbd5e1);
  color: inherit;
}

/* Reminder preview actions: keep yes/no side-by-side */
.vnw-reminderYesNo {
  flex-wrap: nowrap;
}

.vnw-reminderYesNo .vnw-sportsNocYesNoBtn {
  flex: 1 1 0;
  min-width: 0;
}

.vnw-mailDocumentPreviewShell {
  border-radius: 12px;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-panelBg, #fff);
  overflow: hidden;
}

.vnw-mailDocumentPreviewShell.vnw-mailDocumentPreview {
  box-shadow: none;
}

.vnw-mailDocumentPreviewShell .vnw-sportsNocActionCards {
  margin: 0;
  padding: 10px 12px 12px;
  border-top: 1px solid var(--vnw-border, #e2e8f0);
  border-radius: 0;
}

.vnw-secretaryMessageSendQuestion {
  margin: 0;
  padding: 12px 12px 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--vnw-text);
}

.vnw-secretaryMessageYesNo {
  flex-wrap: nowrap;
  margin-top: 0;
  border-top: none;
  padding-top: 10px;
}

.vnw-secretaryMessageYesNo .vnw-sportsNocYesNoBtn {
  flex: 1 1 0;
  min-width: 0;
}

.vnw-prescriptionStructuredLink {
  color: var(--vnw-primary);
  font-weight: 600;
  text-decoration: underline;
  word-break: break-all;
}

.vnw-prescriptionStructuredLink:hover {
  text-decoration: none;
}

.vnw-intentUrlDebug {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--vnw-border);
}

.vnw-intentUrlDebugValue {
  word-break: break-all;
  overflow-wrap: anywhere;
  font-weight: 500;
  font-size: 12px;
  user-select: all;
}

.vnw-patientOptions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 85%;
}

.vnw-patientOptions--resolved {
  opacity: 0.88;
  pointer-events: none;
}

.vnw-patientOptionsResolvedNote {
  font-size: 11px;
  color: var(--vnw-mutedText);
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 2px 0 2px;
}

.vnw-patientOptionsShowMore {
  align-self: flex-start;
  margin-top: 4px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px dashed var(--vnw-primary);
  background: color-mix(in srgb, var(--vnw-primary) 8%, var(--vnw-panelBg));
  color: var(--vnw-primary);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 120ms ease,
    border-color 120ms ease;
}

.vnw-patientOptionsShowMore:hover:not(:disabled) {
  background: color-mix(in srgb, var(--vnw-primary) 14%, var(--vnw-panelBg));
}

.vnw-patientOptionsShowMore:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-patientOptionsShowMoreMeta {
  font-weight: 500;
  color: var(--vnw-mutedText);
}

.vnw-patientOptionBtn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
  color: var(--vnw-text);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition:
    background 120ms ease,
    border-color 120ms ease,
    transform 100ms ease;
}

.vnw-patientOptionBtn:hover:not(:disabled) {
  border-color: var(--vnw-primary);
  background: #f0f6ff;
}

.vnw-patientOptionBtn:active:not(:disabled) {
  transform: scale(0.99);
}

.vnw-patientOptionBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-patientOptionLabel {
  font-weight: 650;
  line-height: 1.25;
}

.vnw-patientOptionHeader {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vnw-patientOptionNumber {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--vnw-primary) 14%, var(--vnw-panelBg));
  color: var(--vnw-primary);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.vnw-patientOptionMeta {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: var(--vnw-mutedText);
  font-variant-numeric: tabular-nums;
}

.vnw-patientOptionMetaPrefix {
  font-weight: 650;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vnw-mutedText);
  opacity: 0.92;
}

.vnw-patientOptionMetaValue {
  font-weight: 500;
}

.vnw-typingDot {
  display: inline-block;
  opacity: 0.7;
  animation: vnw-typing 1s infinite;
}
.vnw-typingDot:nth-child(2) {
  animation-delay: 150ms;
}
.vnw-typingDot:nth-child(3) {
  animation-delay: 300ms;
}

@keyframes vnw-typing {
  0% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-2px);
    opacity: 1;
  }
  60% {
    transform: translateY(0);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }
}

.vnw-composer {
  flex: 0 0 auto;
  padding: 10px;
  border-top: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
}

.vnw-composerTopRow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 6px;
}

/** Tiny wake-phrase reminder under mic row (EN/FR + Hey Medico). */
.vnw-wakePhraseHintComposer {
  font-size: 10px;
  line-height: 1.4;
  color: var(--vnw-mutedText);
  margin: 0 0 8px 0;
  padding: 0 2px 0 0;
  max-width: 100%;
}

.vnw-wakePhraseHintComposerLine {
  display: block;
  word-break: break-word;
}

.vnw-wakePhraseHintComposerLine + .vnw-wakePhraseHintComposerLine {
  margin-top: 2px;
}

.vnw-wakePhraseHintComposerKw {
  font-weight: 600;
  margin-right: 4px;
  opacity: 0.92;
}

.vnw-wakePhraseHintComposerQuote {
  opacity: 0.95;
}

.vnw-micCluster {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

/** Fixed-width slot so the waveform never shifts the mic or send button when it appears */
.vnw-micWaveformSlot {
  width: 30px;
  min-width: 30px;
  height: 22px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;
}

/** Waveform beside mic — replaces “connecting / recording / …” copy for calmer UX */
.vnw-micWaveform {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 3px;
  height: 22px;
  min-width: 26px;
}

.vnw-micWaveformBar {
  display: block;
  width: 3px;
  border-radius: 2px;
  background: var(--vnw-primary);
  height: 14px;
  transform-origin: bottom center;
}

.vnw-micWaveform--prep .vnw-micWaveformBar {
  opacity: 0.75;
  animation: vnw-wfPrep 1s ease-in-out infinite;
}

.vnw-micWaveform--live .vnw-micWaveformBar:nth-child(1) {
  animation: vnw-wfLiveA 0.48s ease-in-out infinite;
}
.vnw-micWaveform--live .vnw-micWaveformBar:nth-child(2) {
  animation: vnw-wfLiveB 0.52s ease-in-out 0.08s infinite;
}
.vnw-micWaveform--live .vnw-micWaveformBar:nth-child(3) {
  animation: vnw-wfLiveC 0.46s ease-in-out 0.15s infinite;
}
.vnw-micWaveform--live .vnw-micWaveformBar:nth-child(4) {
  animation: vnw-wfLiveD 0.5s ease-in-out 0.05s infinite;
}

.vnw-micWaveform--process .vnw-micWaveformBar {
  background: var(--vnw-primary);
  opacity: 0.7;
  animation: vnw-wfProcess 0.95s ease-in-out infinite;
}

@keyframes vnw-wfPrep {
  0%,
  100% {
    transform: scaleY(0.55);
    opacity: 0.5;
  }
  50% {
    transform: scaleY(1);
    opacity: 0.95;
  }
}

@keyframes vnw-wfLiveA {
  0%,
  100% {
    height: 5px;
  }
  50% {
    height: 18px;
  }
}
@keyframes vnw-wfLiveB {
  0%,
  100% {
    height: 8px;
  }
  50% {
    height: 20px;
  }
}
@keyframes vnw-wfLiveC {
  0%,
  100% {
    height: 6px;
  }
  50% {
    height: 16px;
  }
}
@keyframes vnw-wfLiveD {
  0%,
  100% {
    height: 7px;
  }
  50% {
    height: 19px;
  }
}

@keyframes vnw-wfProcess {
  0%,
  100% {
    transform: scaleY(0.5);
    opacity: 0.45;
  }
  50% {
    transform: scaleY(0.95);
    opacity: 0.85;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vnw-micWaveform--prep .vnw-micWaveformBar,
  .vnw-micWaveform--live .vnw-micWaveformBar,
  .vnw-micWaveform--process .vnw-micWaveformBar {
    animation: none;
    height: 10px;
    opacity: 0.8;
    transform: none;
  }
}

.vnw-liveRow {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 10px;
  margin: 0 0 8px 0;
  border: 1px dashed var(--vnw-border);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--vnw-mutedText);
  font-size: 12px;
  line-height: 1.35;
}

.vnw-livePill {
  flex: 0 0 auto;
  font-weight: 650;
  font-size: 11px;
  letter-spacing: 0.2px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eaf2ff;
  border: 1px solid #cfe0ff;
  color: var(--vnw-text);
}

.vnw-liveText {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vnw-micBtn,
.vnw-sendBtn,
.vnw-gttsBtn {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-buttonBg);
  color: var(--vnw-buttonText);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.vnw-micBtn {
  touch-action: manipulation;
  user-select: none;
}

.vnw-micBtn:disabled,
.vnw-sendBtn:disabled,
.vnw-gttsBtn:disabled {
  background: var(--vnw-buttonDisabledBg);
  color: var(--vnw-buttonDisabledText);
  cursor: not-allowed;
}

.vnw-micBtn--recording {
  background: var(--vnw-primary);
  color: var(--vnw-primaryText);
  border-color: rgba(11, 91, 211, 0.45);
  box-shadow:
    0 0 0 2px rgba(11, 91, 211, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transform: scale(0.96);
}

.vnw-micBtn--busy {
  opacity: 0.92;
  box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.45);
}

.vnw-livePill--busy {
  animation: vnw-livePulse 1.1s ease-in-out infinite;
}

.vnw-livePill--ready {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

@keyframes vnw-livePulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.vnw-micBtn:focus-visible,
.vnw-sendBtn:focus-visible {
  outline: 3px solid var(--vnw-primary);
  outline-offset: 3px;
}

.vnw-sendBtn {
  margin-left: auto;
  width: 46px;
}

.vnw-spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 3px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  animation: vnw-spin 600ms linear infinite;
}

@keyframes vnw-spin {
  to {
    transform: rotate(360deg);
  }
}

.vnw-textarea {
  width: 100%;
  resize: none;
  border-radius: 12px;
  border: 1px solid var(--vnw-textareaBorder);
  background: var(--vnw-textareaBg);
  color: var(--vnw-text);
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.35;
  outline: none;
}

.vnw-textarea:focus-visible {
  border-color: var(--vnw-primary);
  box-shadow: 0 0 0 3px rgba(11, 91, 211, 0.18);
}

.vnw-textarea--voiceLive {
  border-color: rgba(11, 91, 211, 0.4);
  background: rgba(11, 91, 211, 0.07);
}

.vnw-textareaBottomLabel {
  margin-top: 6px;
  padding: 0 2px;
  font-size: 10px;
  line-height: 1.35;
  color: var(--vnw-mutedText);
  text-align: right;
}

/* Wake phrase fallback / help (SpeechRecognition unsupported or mic denied). */
.vnw-wakePhraseNotice {
  position: fixed;
  z-index: 2147482999;
  width: min(360px, calc(100vw - 36px));
  max-width: min(360px, calc(100vw - 36px));
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--vnw-text);
  background: var(--vnw-panelBg);
  border: 1px solid var(--vnw-border);
  border-radius: 12px;
  box-shadow: 0 12px 36px var(--vnw-shadow);
}

.vnw-wakePhraseNotice--error {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #7f1d1d;
}

.vnw-wakePhraseNoticeMain {
  font-weight: 600;
}

.vnw-wakePhraseNoticeSub {
  margin-top: 6px;
  font-size: 11px;
  font-weight: 400;
  color: var(--vnw-mutedText);
  line-height: 1.35;
}

.vnw-wakePhraseNotice--error .vnw-wakePhraseNoticeSub {
  color: #991b1b;
}

.vnw-widgetRoot[data-position='bottom-right'] .vnw-wakePhraseNotice {
  right: 18px;
  bottom: 86px;
}

.vnw-widgetRoot[data-position='bottom-left'] .vnw-wakePhraseNotice {
  left: 18px;
  bottom: 86px;
}

.vnw-widgetRoot[data-position='top-right'] .vnw-wakePhraseNotice {
  right: 18px;
  top: 86px;
}

.vnw-widgetRoot[data-position='top-left'] .vnw-wakePhraseNotice {
  left: 18px;
  top: 86px;
}

/* Wake phrase hint inside the open chat panel (not fixed to viewport). */
.vnw-wakePhraseNotice--inPanel {
  position: relative;
  z-index: auto;
  width: 100%;
  max-width: none;
  margin: 0 0 10px;
  box-shadow: none;
  border-radius: 10px;
}

/* Dev-only: shows what wake STT currently hears. */
.vnw-wakeHeardDebug {
  position: fixed;
  z-index: 2147482999;
  width: min(420px, calc(100vw - 36px));
  max-width: min(420px, calc(100vw - 36px));
  padding: 8px 10px;
  font-size: 11px;
  line-height: 1.35;
  color: var(--vnw-mutedText);
  background: var(--vnw-panelBg);
  border: 1px dashed var(--vnw-border);
  border-radius: 10px;
  box-shadow: 0 8px 20px var(--vnw-shadow);
  word-break: break-word;
}

.vnw-wakeHeardDebugLabel {
  color: var(--vnw-text);
  font-weight: 600;
  margin-right: 4px;
}

.vnw-widgetRoot[data-position='bottom-right'] .vnw-wakeHeardDebug {
  right: 18px;
  bottom: 42px;
}

.vnw-widgetRoot[data-position='bottom-left'] .vnw-wakeHeardDebug {
  left: 18px;
  bottom: 42px;
}

.vnw-widgetRoot[data-position='top-right'] .vnw-wakeHeardDebug {
  right: 18px;
  top: 42px;
}

.vnw-widgetRoot[data-position='top-left'] .vnw-wakeHeardDebug {
  left: 18px;
  top: 42px;
}

.vnw-wakeHeardDebug--inPanel {
  position: relative;
  z-index: auto;
  width: 100%;
  max-width: none;
  margin: 0 0 10px;
  box-shadow: none;
}

.vnw-messageBodyText {
  margin-bottom: 8px;
}

.vnw-secretaryEntities {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--vnw-border, #e2e8f0);
}

.vnw-secretaryEntities .vnw-prescriptionStructuredValue {
  word-break: break-word;
}

/* Secretary / message intent: conversational preview (not key-value rows). */
.vnw-secretaryMessagePreview {
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-panelSubtleBg, #f8fafc);
  text-align: left;
}

.vnw-secretaryMessagePreviewRow {
  display: grid;
  /* label | value | Edit (same idea as .vnw-sportsNocKvRow — third child must not wrap below) */
  grid-template-columns: minmax(4.75rem, 6.5rem) minmax(0, 1fr) auto;
  gap: 8px 10px;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.45;
  min-width: 0;
}

.vnw-secretaryMessagePreviewRow .vnw-sportsNocKvAction {
  justify-self: end;
}

.vnw-secretaryMessagePreviewRow:last-of-type {
  margin-bottom: 0;
}

.vnw-secretaryMessagePreviewRow--body {
  align-items: start;
}

.vnw-secretaryMessagePreviewRow--body .vnw-secretaryMessagePreviewValue {
  white-space: pre-wrap;
  word-break: break-word;
}

.vnw-secretaryMessagePreviewRow--body .vnw-sportsNocKvAction {
  align-self: start;
}

.vnw-secretaryMessagePreviewRow--inline {
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
  align-items: start;
}

.vnw-secretaryMessagePreviewRow--inline > .vnw-secretaryMessagePreviewLabel {
  display: none;
}

.vnw-secretaryInlineEditBody {
  grid-column: auto;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

.vnw-secretaryInlineFieldStack {
  width: clamp(18rem, 50%, 28rem);
  max-width: 100%;
}

.vnw-secretaryInlineEditInput {
  min-height: 26px;
  padding-top: 6px;
  padding-bottom: 6px;
  line-height: 1.45;
  resize: none;
}

.vnw-secretaryInlineEditInput.vnw-reminderInlineEditInput {
  width: 100%;
}

.vnw-secretaryInlineEditInput--recipient {
  white-space: pre;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior: contain;
  touch-action: pan-x;
}

.vnw-secretaryInlineDropdown {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 0;
  padding: 6px;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-top: none;
  border-radius: 0 0 10px 10px;
  background: color-mix(in srgb, var(--vnw-panelSubtleBg, #f8fafc) 80%, var(--vnw-panelBg, #fff));
  max-height: 220px;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.vnw-secretaryInlineDropdownOption {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--vnw-panelBg, #fff);
  color: var(--vnw-text, #0f172a);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.vnw-secretaryInlineDropdownOption:hover:not(:disabled) {
  border-color: var(--vnw-primary);
  background: #f0f6ff;
}

.vnw-secretaryInlineDropdownOption:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-secretaryInlineDropdownPrimary {
  font-weight: 600;
  line-height: 1.3;
}

.vnw-secretaryInlineDropdownMeta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  color: var(--vnw-mutedText, #64748b);
  font-variant-numeric: tabular-nums;
}

.vnw-secretaryInlineDropdownMetaLabel {
  font-size: 10px;
  font-weight: 650;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.vnw-secretaryMessagePreviewLabel {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--vnw-mutedText, #64748b);
}

.vnw-secretaryMessagePreviewValue {
  color: var(--vnw-text, #0f172a);
}

.vnw-secretaryMessagePreviewValue--emphasis {
  font-weight: 600;
  color: var(--vnw-text, #0f172a);
}

.vnw-secretaryMessagePreviewValue--toggle {
  display: flex;
  align-items: center;
}

.vnw-secretaryMessagePreviewFoot {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--vnw-border, #e2e8f0);
  font-size: 13px;
  line-height: 1.4;
}

.vnw-secretaryMessagePreviewFootLabel {
  font-weight: 600;
}

.vnw-secretaryMessagePreviewFoot--success {
  color: var(--vnw-successText, #15803d);
}

.vnw-secretaryMessagePreviewFoot--error {
  color: var(--vnw-errorText, #b91c1c);
}

.vnw-secretaryToggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 40px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--vnw-border, #cbd5e1);
  border-radius: 999px;
  background: #cbd5e1;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.vnw-secretaryToggle:hover {
  border-color: var(--vnw-primary, #0b5bd3);
}

.vnw-secretaryToggle:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(11, 91, 211, 0.18);
}

.vnw-secretaryToggle--on {
  background: var(--vnw-primary, #0b5bd3);
  border-color: var(--vnw-primary, #0b5bd3);
}

.vnw-secretaryToggleKnob {
  width: 16px;
  height: 16px;
  margin-left: 3px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
  transition: transform 0.15s ease;
}

.vnw-secretaryToggle--on .vnw-secretaryToggleKnob {
  transform: translateX(18px);
}

/* Yes / No chips when the server is awaiting send confirmation */
.vnw-binaryConfirmBar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  align-items: center;
  padding: 10px 14px 12px;
  border-top: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-textareaBg, #f8fafc);
}

.vnw-binaryConfirmBtn {
  min-width: 120px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--vnw-border, #e2e8f0);
  background: var(--vnw-panelBg, #fff);
  color: var(--vnw-text, #0f172a);
}

.vnw-binaryConfirmBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.vnw-binaryConfirmBtn--yes {
  border-color: var(--vnw-primary, #0b5bd3);
  background: var(--vnw-primary, #0b5bd3);
  color: var(--vnw-primaryText, #fff);
}

.vnw-binaryConfirmBtn--no {
  background: var(--vnw-panelBg, #fff);
}

/* --- Unread / urgent messages (in-chat cards) --- */
.vnw-unreadBlock {
  margin-top: 2px;
  text-align: left;
}

.vnw-unreadBlockTitle {
  font-weight: 600;
  margin-bottom: 10px;
}

.vnw-unreadBlock--fallback .vnw-unreadBlockFallback {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--vnw-mutedText);
  line-height: 1.4;
}

.vnw-unreadList {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.vnw-unreadList--loading,
.vnw-unreadList--error,
.vnw-unreadList--empty {
  padding: 12px 10px;
  border-radius: 12px;
  border: 1px dashed var(--vnw-border);
  background: var(--vnw-textareaBg);
}

.vnw-unreadListSpinner {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 3px solid rgba(11, 91, 211, 0.2);
  border-top-color: var(--vnw-primary);
  animation: vnw-spin 700ms linear infinite;
  margin: 0 auto 8px;
}

.vnw-unreadListLoadingText {
  margin: 0;
  font-size: 12px;
  color: var(--vnw-mutedText);
  text-align: center;
}

.vnw-unreadListErrorText {
  margin: 0 0 8px;
  font-size: 12px;
  color: #b91c1c;
  line-height: 1.4;
}

.vnw-unreadListRetry {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
  color: var(--vnw-primary);
  cursor: pointer;
}

.vnw-unreadListRetry:hover {
  border-color: var(--vnw-primary);
}

.vnw-unreadListEmptyTitle {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--vnw-text);
}

.vnw-unreadListEmptySub {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--vnw-mutedText);
}

.vnw-unreadListItem {
  width: 100%;
}

.vnw-unreadListWrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.vnw-unreadReadAloudPrompt {
  margin-top: 4px;
  padding: 12px 12px 10px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-textareaBg, #f8fafc);
}

.vnw-unreadReadAloudPromptText {
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--vnw-text);
  font-weight: 500;
}

.vnw-unreadReadAloudHint {
  margin: 10px 0 0;
  font-size: 11px;
  line-height: 1.35;
  color: var(--vnw-mutedText);
}

.vnw-unreadReadAloudPromptBtns {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.vnw-unreadReadAloudBtn {
  flex: 1 1 120px;
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 650;
  cursor: pointer;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
  color: var(--vnw-text);
}

.vnw-unreadReadAloudBtn--yes {
  border-color: var(--vnw-primary);
  color: var(--vnw-primary);
}

.vnw-unreadReadAloudBtn--yes:hover {
  background: var(--vnw-primary);
  color: var(--vnw-primaryText, #fff);
}

.vnw-unreadReadAloudBtn--no:hover {
  border-color: var(--vnw-mutedText);
}

.vnw-unreadCard {
  width: 100%;
  display: block;
  text-align: left;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid var(--vnw-border);
  background: var(--vnw-panelBg);
  color: var(--vnw-text);
  cursor: pointer;
  font: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
}

.vnw-unreadCard--static {
  cursor: default;
}

.vnw-unreadCard--static:hover,
.vnw-unreadCard--static:active {
  border-color: var(--vnw-border);
  box-shadow: none;
  transform: none;
}

.vnw-unreadCard--static.vnw-unreadCard--urgent:hover {
  border-color: rgba(220, 38, 38, 0.45);
}

.vnw-unreadCard:hover {
  border-color: rgba(11, 91, 211, 0.45);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.vnw-unreadCard:focus-visible {
  outline: none;
  border-color: var(--vnw-primary);
  box-shadow: 0 0 0 3px rgba(11, 91, 211, 0.22);
}

.vnw-unreadCard:active {
  transform: translateY(1px);
}

.vnw-unreadCard--urgent {
  border-color: rgba(220, 38, 38, 0.45);
  background: linear-gradient(135deg, rgba(254, 226, 226, 0.55), var(--vnw-panelBg));
  box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.12);
}

.vnw-unreadCard--urgent:hover {
  border-color: rgba(185, 28, 28, 0.65);
}

.vnw-unreadCardHeader {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.vnw-unreadCardSender {
  font-weight: 650;
  font-size: 13px;
  color: var(--vnw-text);
}

.vnw-unreadCardBadges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}

.vnw-unreadBadge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 6px;
  line-height: 1.2;
}

.vnw-unreadBadge--unread {
  background: rgba(11, 91, 211, 0.12);
  color: var(--vnw-primary);
}

.vnw-unreadBadge--urgent {
  background: rgba(220, 38, 38, 0.15);
  color: #b91c1c;
}

.vnw-unreadCardRecipient {
  font-size: 12px;
  color: var(--vnw-mutedText);
  margin-top: 4px;
}

.vnw-unreadCardSubject {
  font-size: 12px;
  font-weight: 600;
  color: var(--vnw-text);
  margin-bottom: 4px;
}

.vnw-unreadCardPreview {
  font-size: 12px;
  color: var(--vnw-mutedText);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.vnw-unreadCardMeta {
  margin-top: 8px;
  font-size: 11px;
  color: var(--vnw-mutedText);
}
